/**
 * API Helper Utilities
 * Reduces code duplication by providing generic API request wrappers
 * with automatic error handling and audit logging
 */

import config from '../config';
import { authenticatedFetch } from './authService';

// ============================================================================
// GENERIC API REQUEST WRAPPER
// ============================================================================

/**
 * Get current tenant endpoints
 * This is set by the tenant context
 */
let currentTenantConfig = null;

export const setTenantConfig = (tenantConfig) => {
    currentTenantConfig = tenantConfig;
};

const getCurrentEndpoints = () => {
    if (currentTenantConfig) {
        return {
            litellm: currentTenantConfig.litellmUrl,
            openwebui: currentTenantConfig.openwebuiUrl,
            tenantId: currentTenantConfig.id
        };
    }

    return {
        litellm: config.apiBaseUrl,
        openwebui: config.openWebUIBaseUrl,
        tenantId: 'default'
    };
};

/**
 * Log an audit entry to the backend
 */
const logAuditEntry = async (entry) => {
    try {
        const response = await authenticatedFetch(`${config.backendApiUrl}/audit-log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: entry.action,
                user_email: entry.userEmail,
                details: entry.details,
                timestamp: new Date().toISOString()
            }),
        });
        if (!response.ok) {
            throw new Error(`Audit log failed: ${response.status}`);
        }
        return { success: true };
    } catch (error) {
        console.error('[AUDIT] Failed to log entry:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Generic API action creator
 * Eliminates repetitive try-catch-audit patterns
 *
 * @param {Object} config - Configuration object
 * @param {string} config.name - Action name for logging
 * @param {Function} config.endpoint - Function that returns endpoint URL
 * @param {string} config.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} config.service - Service type ('openwebui' or 'litellm')
 * @param {Function} config.buildRequest - Function to build request body
 * @param {string} config.auditAction - Audit action name (optional)
 * @param {Function} config.buildAuditDetails - Function to build audit details (optional)
 * @returns {Function} API action function
 */
export const createApiAction = (actionConfig) => {
    return async (...args) => {
        try {
            const endpoints = getCurrentEndpoints();
            const baseUrl = actionConfig.service === 'litellm' ? endpoints.litellm : endpoints.openwebui;

            const url = typeof actionConfig.endpoint === 'function'
                ? `${baseUrl}${actionConfig.endpoint(...args)}`
                : `${baseUrl}${actionConfig.endpoint}`;

            const requestOptions = {
                method: actionConfig.method || 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            };

            if (actionConfig.buildRequest && actionConfig.method !== 'GET') {
                requestOptions.body = JSON.stringify(actionConfig.buildRequest(...args));
            }

            // Use authenticatedFetch to include Azure AD token for backend proxy
            const response = await authenticatedFetch(url, requestOptions);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const data = await response.json();

            // Automatic audit logging if configured
            if (actionConfig.auditAction && actionConfig.buildAuditDetails) {
                const userEmail = args.find(arg => typeof arg === 'string' && arg.includes('@'));
                if (userEmail) {
                    await logAuditEntry({
                        action: actionConfig.auditAction,
                        userEmail: userEmail,
                        details: actionConfig.buildAuditDetails(...args)
                    });
                }
            }

            return { success: true, data };

        } catch (error) {
            return { success: false, error: error.message };
        }
    };
};

// ============================================================================
// USER ACCOUNT DISABLE/ENABLE (GROUP-BASED)
// ============================================================================
// OpenWebUI doesn't have a native "disable account" feature.
// We implement it by removing all groups from a user (disabling)
// and restoring them when re-enabling.
// The original groups are stored in the backend for restoration.
// ============================================================================

/**
 * Get user's current groups from OpenWebUI
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data including groups
 */
export const getUserById = async (userId) => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/users/${userId}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Check if a user is disabled (stored in backend)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Disabled status and stored groups
 */
export const getDisabledUserStatus = async (userId) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/disabled-users/${userId}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return { success: false, error: error.message, isDisabled: false };
    }
};

/**
 * Store disabled user's groups in backend
 * @param {string} userId - User ID
 * @param {Array} groups - User's current groups
 * @param {string} email - User's email
 * @param {string} disabledBy - Admin who disabled
 * @returns {Promise<Object>} Result
 */
const storeDisabledUserGroups = async (userId, groups, email, disabledBy) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/disabled-users/${userId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    groups,
                    email,
                    disabledBy,
                    disabledAt: new Date().toISOString()
                })
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Remove disabled user record from backend
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Stored groups data
 */
const removeDisabledUserRecord = async (userId) => {
    try {
        const response = await authenticatedFetch(
            `${config.backendApiUrl}/disabled-users/${userId}`,
            {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Remove user from a specific group
 * @param {string} groupId - Group ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Result
 */
const removeUserFromGroup = async (groupId, userId) => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/groups/id/${groupId}/users/remove`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_ids: [userId] })
            }
        );

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Add user to a specific group
 * @param {string} groupId - Group ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Result
 */
const addUserToGroup = async (groupId, userId) => {
    try {
        const endpoints = getCurrentEndpoints();
        const response = await authenticatedFetch(
            `${endpoints.openwebui}/api/v1/groups/id/${groupId}/users/add`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_ids: [userId] })
            }
        );

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Disable a user account by removing them from all groups
 * Stores original groups in backend for later restoration
 *
 * @param {string} userId - User ID to disable
 * @param {string} adminEmail - Admin performing the action
 * @param {string} targetUserEmail - Email of user being disabled
 * @returns {Promise<Object>} Result
 */
export const disableUserAccount = async (userId, adminEmail = null, targetUserEmail = null) => {
    try {
        // 1. Get user's current groups
        const userResult = await getUserById(userId);
        if (!userResult.success) {
            throw new Error(`Failed to get user: ${userResult.error}`);
        }

        const user = userResult.data;
        const groups = user.groups || [];

        if (groups.length === 0) {
            // User already has no groups - check if already disabled
            const disabledStatus = await getDisabledUserStatus(userId);
            if (disabledStatus.isDisabled) {
                return { success: false, error: 'User is already disabled' };
            }
            // Store empty groups record anyway to mark as disabled
        }

        // 2. Store groups in backend before removing
        const storeResult = await storeDisabledUserGroups(
            userId,
            groups,
            targetUserEmail || user.email,
            adminEmail
        );

        if (!storeResult.success) {
            throw new Error(`Failed to store groups: ${storeResult.error}`);
        }

        // 3. Remove user from all groups
        const removeErrors = [];
        for (const group of groups) {
            const result = await removeUserFromGroup(group.id, userId);
            if (!result.success) {
                removeErrors.push(`${group.name}: ${result.error}`);
            }
        }

        if (removeErrors.length > 0 && removeErrors.length === groups.length) {
            // All removals failed - rollback
            await removeDisabledUserRecord(userId);
            throw new Error(`Failed to remove from groups: ${removeErrors.join(', ')}`);
        }

        // 4. Log audit entry
        if (adminEmail) {
            await logAuditEntry({
                action: 'disable_user_account',
                userEmail: adminEmail,
                details: {
                    user_id: userId,
                    user_email: targetUserEmail || user.email,
                    groups_removed: groups.map(g => g.name).join(', '),
                    reason: 'Account disabled via dashboard'
                }
            });
        }

        return {
            success: true,
            data: {
                groupsRemoved: groups.length,
                partialErrors: removeErrors.length > 0 ? removeErrors : undefined
            }
        };

    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Enable a user account by restoring their original groups
 *
 * @param {string} userId - User ID to enable
 * @param {string} adminEmail - Admin performing the action
 * @param {string} targetUserEmail - Email of user being enabled
 * @returns {Promise<Object>} Result
 */
export const enableUserAccount = async (userId, adminEmail = null, targetUserEmail = null) => {
    try {
        // 1. Get stored groups from backend
        const disabledStatus = await getDisabledUserStatus(userId);

        if (!disabledStatus.success) {
            throw new Error(`Failed to get disabled status: ${disabledStatus.error}`);
        }

        if (!disabledStatus.isDisabled) {
            return { success: false, error: 'User is not disabled' };
        }

        const storedData = disabledStatus.data;
        const groups = storedData.groups || [];

        // 2. Add user back to all groups
        const addErrors = [];
        for (const group of groups) {
            const result = await addUserToGroup(group.id, userId);
            if (!result.success) {
                addErrors.push(`${group.name}: ${result.error}`);
            }
        }

        // 3. Remove disabled user record from backend
        await removeDisabledUserRecord(userId);

        // 4. Log audit entry
        if (adminEmail) {
            await logAuditEntry({
                action: 'enable_user_account',
                userEmail: adminEmail,
                details: {
                    user_id: userId,
                    user_email: targetUserEmail || storedData.email,
                    groups_restored: groups.map(g => g.name).join(', '),
                    reason: 'Account enabled via dashboard'
                }
            });
        }

        return {
            success: true,
            data: {
                groupsRestored: groups.length,
                partialErrors: addErrors.length > 0 ? addErrors : undefined
            }
        };

    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ============================================================================
// PRE-CONFIGURED API ACTIONS
// ============================================================================

/**
 * Add user to group
 * Also clears any disabled status for the user (in case they were previously disabled)
 */
export const addUserToGroupAction = async (groupId, userId, userEmail, groupName, targetUserEmail) => {
    try {
        const endpoints = getCurrentEndpoints();
        const url = `${endpoints.openwebui}/api/v1/groups/id/${groupId}/users/add`;

        const response = await authenticatedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: [userId] })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();

        // Clear any disabled status for this user since they're being re-added
        // This handles the case where a user was disabled (removed from all groups)
        // and is now being manually added back via the "Add User" flow
        await removeDisabledUserRecord(userId);

        // Audit logging
        if (userEmail) {
            await logAuditEntry({
                action: 'add_user_to_team',
                userEmail: userEmail,
                details: {
                    group_id: groupId,
                    group_name: groupName || groupId,
                    user_id: userId,
                    user_email: targetUserEmail || userId
                }
            });
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Remove user from group
 * Note: OpenWebUI expects "user_ids" as an array (plural - "Remove Users From Group")
 */
export const removeUserFromGroupAction = createApiAction({
    name: 'removeUserFromGroup',
    service: 'openwebui',
    endpoint: (groupId) => `/api/v1/groups/id/${groupId}/users/remove`,
    method: 'POST',
    buildRequest: (groupId, userId) => ({ user_ids: [userId] }),
    auditAction: 'remove_user_from_team',
    buildAuditDetails: (groupId, userId, userEmail, groupName, targetUserEmail) => ({
        group_id: groupId,
        group_name: groupName || groupId,
        user_id: userId,
        user_email: targetUserEmail || userId
    })
});

/**
 * Block team
 */
export const blockTeamAction = createApiAction({
    name: 'blockTeam',
    service: 'litellm',
    endpoint: '/team/block',
    method: 'POST',
    buildRequest: (teamId) => ({ team_id: teamId }),
    auditAction: 'block_team',
    buildAuditDetails: (teamId, userEmail, teamName, reason) => ({
        team_id: teamId,
        team_name: teamName || teamId,
        reason: reason || 'Manual block'
    })
});

/**
 * Unblock team
 */
export const unblockTeamAction = createApiAction({
    name: 'unblockTeam',
    service: 'litellm',
    endpoint: '/team/unblock',
    method: 'POST',
    buildRequest: (teamId) => ({ team_id: teamId }),
    auditAction: 'unblock_team',
    buildAuditDetails: (teamId, userEmail, teamName) => ({
        team_id: teamId,
        team_name: teamName || teamId
    })
});

// ============================================================================
// EXPORT TENANT CONFIG SETTER (for compatibility with existing code)
// ============================================================================

export { getCurrentEndpoints, logAuditEntry };
